import { useEffect, useState } from 'react';
import { X, AlertCircle } from 'lucide-react';
import { dashboardApi, type PartInUse } from '@/api/dashboardApi';
import Toast from '@/components/ui/Toast';

interface PartsUtilizationModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function PartsUtilizationModal({ isOpen, onClose }: PartsUtilizationModalProps) {
  const [parts, setParts] = useState<PartInUse[]>([]);
  const [selectedPart, setSelectedPart] = useState<PartInUse | null>(null);
  const [loading, setLoading] = useState(false);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [totalPartsCount, setTotalPartsCount] = useState(0);
  const [activeJobsCount, setActiveJobsCount] = useState(0);

  useEffect(() => {
    if (isOpen) {
      loadPartsInUse();
    }
  }, [isOpen]);

  async function loadPartsInUse() {
    setLoading(true);
    try {
      const response = await dashboardApi.getPartsInUse();
      console.log('Parts in use response:', response);
      setParts(response.parts || []);
      setTotalPartsCount(response.totalPartsInUse);
      setActiveJobsCount(response.activeJobs);
      if (response.parts?.length > 0) {
        setSelectedPart(response.parts[0]);
      } else {
        console.warn('No parts in response');
      }
    } catch (error) {
      console.error('Failed to load parts in use:', error);
      setToastMessage('Failed to load parts data');
    } finally {
      setLoading(false);
    }
  }

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-5xl max-h-[80vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div>
            <h2 className="text-xl font-bold">Parts in Use</h2>
            <p className="text-sm text-slate-600 mt-1">{totalPartsCount} parts across {activeJobsCount} active jobs</p>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-slate-100 rounded-lg transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex flex-1 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center w-full">
              <div className="animate-spin w-6 h-6 border-3 border-primary border-t-transparent rounded-full" />
            </div>
          ) : (
            <>
              {/* Left Panel - Parts List */}
              <div className="w-96 border-r border-slate-200 overflow-y-auto bg-slate-50">
                {parts.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full text-slate-400 p-6">
                    <AlertCircle className="h-8 w-8 mb-2 opacity-50" />
                    <p className="text-sm">No parts currently in use</p>
                  </div>
                ) : (
                  <div className="divide-y divide-slate-200">
                    {parts.map((part) => (
                      <button
                        key={part.partId}
                        onClick={() => setSelectedPart(part)}
                        className={`w-full text-left px-4 py-4 transition-colors ${
                          selectedPart?.partId === part.partId
                            ? 'bg-primary text-white'
                            : 'bg-slate-50 hover:bg-slate-100 text-slate-900'
                        }`}
                      >
                        <p className="font-semibold text-sm line-clamp-2">{part.partName}</p>
                        <div className="flex items-center justify-between mt-2">
                          <span className="text-xs font-bold">
                            {part.totalQuantityInUse}x in use
                          </span>
                          <span className="text-xs opacity-75">
                            {part.jobs.length} job{part.jobs.length !== 1 ? 's' : ''}
                          </span>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Right Panel - Part Details */}
              <div className="flex-1 overflow-y-auto p-6">
                {selectedPart ? (
                  <div className="space-y-4">
                    <div>
                      <h3 className="font-bold text-lg mb-2">{selectedPart.partName}</h3>
                      <div className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-blue-100 text-blue-700">
                        <span className="text-sm font-medium">{selectedPart.totalQuantityInUse} units in use</span>
                      </div>
                    </div>

                    <div>
                      <h4 className="font-semibold text-sm mb-3">Being used on:</h4>
                      {selectedPart.jobs.length === 0 ? (
                        <div className="text-center py-8 text-slate-400">
                          <p className="text-sm">No active usage</p>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {selectedPart.jobs.map((job) => (
                            <div
                              key={`${job.workOrderId}-${job.workOrderNumber}`}
                              className="border border-slate-200 rounded-lg p-4 bg-white hover:shadow-md transition-shadow"
                            >
                              <div className="flex items-start justify-between mb-2">
                                <div>
                                  <p className="font-semibold text-sm">#{job.workOrderNumber}</p>
                                  <p className="text-xs text-slate-600 mt-0.5">{job.vehicle}</p>
                                </div>
                                <span className="text-sm font-bold text-amber-600">
                                  {job.quantity}x
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-full text-slate-400">
                    <p className="text-sm">Select a part to view details</p>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {toastMessage && (
        <Toast
          message={toastMessage}
          onClose={() => setToastMessage(null)}
          type="success"
          duration={3000}
        />
      )}
    </div>
  );
}
